import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// LeadConnector chat widget loader config — same values that used to live
// inline in /public/index.html.
const SCRIPT_SRC = "https://widgets.leadconnectorhq.com/loader.js";
const WIDGET_ID = "6a2d8f17b48bf7b6a3ab21e0";

// Pages that already capture a phone number through the GHL form embed.
// A2P 10DLC carrier review treats "form + chat widget on the same page" as
// multiple opt-in sources and rejects the campaign — so we keep the widget
// completely OFF these routes.
//
// Form pages:   /                         (Home hero form)
//               /contact                  (Contact form section)
//               /service-areas[/anything] (Form right of the FL map + area detail)
//               /services/:slug           (Service-detail #quote section)
// Widget-on:    /services (index)        — no form, just service cards
//               /about, /terms, /privacy, 404
function pathHasForm(pathname) {
  if (pathname === "/") return true;
  if (pathname === "/contact") return true;
  if (pathname.startsWith("/service-areas")) return true;
  // /services (bare) is a listing page with no form; /services/<slug> has one.
  if (pathname.startsWith("/services/")) return true;
  return false;
}

// LeadConnector's loader injects an iframe and supporting DOM into <body>.
// We have to actively remove BOTH the loader script(s) and the injected DOM
// when entering a form route, otherwise the widget persists across routes.
function teardown() {
  const selectors = [
    'script[src*="leadconnectorhq.com/loader"]',
    'script[src*="leadconnectorhq.com/chat-widget"]',
    'script[src*="leadconnectorhq.com"]',
    'script[src*="stcdn.leadconnectorhq.com"]',
    'script[data-widget-id="' + WIDGET_ID + '"]',
    'iframe[src*="leadconnectorhq"]',
    'iframe[id*="lc_chat-widget"]',
    '[id^="lc_chat-widget"]',
    '[class*="lc_chat-widget"]',
    '[class*="LeadConnectorChat"]',
    'chat-widget',
  ];
  document.querySelectorAll(selectors.join(",")).forEach((el) => el.remove());
  // The loader also attaches a `window.LeadConnectorChat` global and a
  // setInterval that re-mounts the widget. Reset them so a future page-load
  // is clean and so the bot validator can never observe a chat instance.
  try {
    delete window.LeadConnectorChat;
    delete window.LC_CHAT_WIDGET;
  } catch (_) {
    /* non-configurable in some browsers — best-effort only */
  }
}

function inject() {
  if (document.querySelector(`script[data-fp-chat="1"]`)) return;
  const s = document.createElement("script");
  s.src = SCRIPT_SRC;
  s.setAttribute("data-resources-url", "https://widgets.leadconnectorhq.com/chat-widget/loader.js");
  s.setAttribute("data-widget-id", WIDGET_ID);
  s.setAttribute("data-source", "WEB_USER");
  s.async = true;
  s.dataset.fpChat = "1";
  document.body.appendChild(s);
}

export default function ChatWidget() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathHasForm(pathname)) {
      // Form page — make sure no widget exists, even if a previous page
      // injected one and the loader has already started spawning DOM.
      teardown();
      // Re-run shortly after — LC's loader sometimes appends its widget on a
      // delayed tick after we navigate.
      const t = setTimeout(teardown, 600);
      return () => clearTimeout(t);
    }
    inject();
    return undefined;
  }, [pathname]);

  return null;
}
